import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface DataTableColumn<T> {
  readonly key: string
  readonly header: string
  /** Renderiza a célula da linha. */
  readonly cell: (row: T) => ReactNode
  readonly align?: 'left' | 'right'
  /** Oculta o header visualmente (mantém acessível) — útil para a coluna de ações. */
  readonly headerSrOnly?: boolean
  readonly cellClassName?: string
}

export interface DataTableProps<T> {
  readonly columns: readonly DataTableColumn<T>[]
  readonly rows: readonly T[]
  readonly rowKey: (row: T) => string
  /** Legenda acessível da tabela (sr-only). */
  readonly caption?: string
  /** Largura mínima antes de rolar horizontalmente (ex.: 'min-w-[640px]'). */
  readonly minWidthClassName?: string
  readonly onRowClick?: (row: T) => void
}

/**
 * Tabela de dados reutilizável: markup consistente (header + linhas), rolagem horizontal própria
 * (o body da página nunca rola de lado) e semântica acessível. As células vêm de funções `cell` das
 * colunas, então células interativas (botões, selects) são suportadas.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  minWidthClassName = 'min-w-[640px]',
  onRowClick,
}: DataTableProps<T>) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border-default">
      <table className={cn('w-full text-left text-sm', minWidthClassName)}>
        {caption !== undefined ? <caption className="sr-only">{caption}</caption> : null}
        <thead className="border-b border-border-default bg-background-subtle text-xs uppercase tracking-wide text-foreground-muted">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn('px-4 py-2 font-medium', column.align === 'right' && 'text-right')}
              >
                {column.headerSrOnly === true ? (
                  <span className="sr-only">{column.header}</span>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-default">
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={cn(
                'align-top',
                onRowClick !== undefined && 'cursor-pointer hover:bg-background-subtle',
              )}
              onClick={
                onRowClick !== undefined
                  ? () => {
                      onRowClick(row)
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    'px-4 py-2',
                    column.align === 'right' && 'text-right',
                    column.cellClassName,
                  )}
                >
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
